import { getHTMLFromFragment } from '@tiptap/core'
import { DOMParser as PMDOMParser, Fragment, Slice } from '@tiptap/pm/model'

const CONTEXT_BEFORE_CHARS = 6000
const CONTEXT_AFTER_CHARS = 2000

// Models sometimes wrap HTML in a Markdown code fence despite instructions
export const stripFences = html => html
  .replace(/^\s*```[a-z]*\s*/i, '')
  .replace(/\s*```\s*$/, '')

// Parses AI-written HTML with the editor schema, which drops anything the
// editor doesn't support. The HTML is parsed in an inert document so
// nothing in it (scripts, image loads, event handlers) runs. parseSlice,
// unlike parse, doesn't pad empty HTML into an empty paragraph.
export const parseHtml = (schema, html) => {
  const body = new window.DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body
  return PMDOMParser.fromSchema(schema).parseSlice(body).content
}

// Slice to put `fragment` between $from and $to. When an end is inside a
// paragraph and the fragment's outer block there is a paragraph too, that
// end is left open so its text merges into the existing paragraph instead
// of splitting it (an edited phrase stays in its sentence).
export const sliceFor = ($from, $to, fragment) => {
  if (!fragment.childCount) {
    return Slice.empty
  }
  const openStart = $from.parent.inlineContent && fragment.firstChild.isTextblock ? 1 : 0
  const openEnd = $to.parent.inlineContent && fragment.lastChild.isTextblock ? 1 : 0
  return new Slice(fragment, openStart, openEnd)
}

// Inline content of a fragment's paragraphs, for previewing an edit that
// sits inside a paragraph. Null when the fragment isn't only textblocks.
export const inlinePreview = (schema, fragment) => {
  const nodes = []
  let textblocksOnly = true
  fragment.forEach((node, offset, index) => {
    if (!node.isTextblock) {
      textblocksOnly = false
      return
    }
    if (index > 0 && schema.nodes.hardBreak) {
      nodes.push(schema.nodes.hardBreak.create())
    }
    node.content.forEach(child => nodes.push(child))
  })
  return textblocksOnly ? Fragment.from(nodes) : null
}

export const fragmentHtml = (editor, fragment) => getHTMLFromFragment(fragment, editor.schema)

// The document as top-level blocks with ids, for the chat agent
export const documentBlocks = (editor) => {
  const blocks = []
  editor.state.doc.forEach((node, offset, index) => {
    blocks.push({
      id: `b${index}`,
      html: fragmentHtml(editor, Fragment.from(node)),
      from: offset,
      to: offset + node.nodeSize
    })
  })
  return blocks
}

// Target of the inline prompt: the selection, or, with a bare cursor, the
// cursor position. A cursor in an empty paragraph targets the whole
// paragraph so the written content replaces it rather than landing in it.
export const promptRange = (state) => {
  const { from, to, empty, $from } = state.selection
  if (empty && $from.parent.isTextblock && $from.parent.content.size === 0 && $from.depth > 0) {
    return { from: $from.before(), to: $from.after() }
  }
  return { from, to }
}

// Plain text around a range, as context for writing at the cursor
export const surroundingText = (doc, from, to) => ({
  before: doc.textBetween(0, from, '\n\n').slice(-CONTEXT_BEFORE_CHARS),
  after: doc.textBetween(to, doc.content.size, '\n\n').slice(0, CONTEXT_AFTER_CHARS)
})

// The same blocks for rendered HTML that isn't in an editor (an advice
// being viewed). Loose top-level text is wrapped in a paragraph.
export const elementBlocks = (element) => {
  const blocks = []
  element.childNodes.forEach((node) => {
    let html = ''
    if (node.nodeType === Node.ELEMENT_NODE) {
      html = node.outerHTML
    } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
      const paragraph = document.createElement('p')
      paragraph.textContent = node.textContent.trim()
      html = paragraph.outerHTML
    }
    if (html) {
      blocks.push({ id: `b${blocks.length}`, html, node })
    }
  })
  return blocks
}

// The page selection's range when it is within `element`: null when the
// selection there is empty, undefined when it is elsewhere
export const rangeWithin = (element) => {
  const selection = window.getSelection()
  if (!element || !selection || !selection.rangeCount) {
    return undefined
  }
  const range = selection.getRangeAt(0)
  if (!element.contains(range.commonAncestorContainer)) {
    return undefined
  }
  return range.collapsed ? null : range.cloneRange()
}

// Blocks containing `range`, as the agent sees them
export const rangeSelection = (range, blocks) => {
  const text = range ? range.toString().trim() : ''
  if (!text) {
    return null
  }
  const touched = blocks.filter(block => range.intersectsNode(block.node))
  return touched.length ?
    { from_id: touched[0].id, to_id: touched[touched.length - 1].id, text } :
    null
}
